import { useRef, useState, type ReactElement } from 'react';
import { Database, Loader2 } from 'lucide-react';
import { Dialog } from '../../components/Dialog.js';
import { apiPostForm, ApiError } from '../../api/client.js';
import { useToast } from '../../hooks/useToast.js';

const DB_PATH = '<ROOT>/data/data.sqlite';
const BACKUP_URL = '/api/admin/backup';

export function DatabaseSection(): ReactElement {
  const toast = useToast();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const anchorRef = useRef<HTMLAnchorElement | null>(null);
  const [downloading, setDownloading] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingFile, setPendingFile] = useState<File | null>(null);

  function openRestorePicker(): void {
    fileInputRef.current?.click();
  }

  function onDownload(): void {
    if (downloading) return;
    setDownloading(true);
    try {
      anchorRef.current?.click();
    } finally {
      toast.push('info', 'Preparing backup download…');
      window.setTimeout(() => setDownloading(false), 600);
    }
  }

  function onFileChange(ev: React.ChangeEvent<HTMLInputElement>): void {
    const file = ev.target.files?.[0] ?? null;
    if (!file) return;
    setPendingFile(file);
    setConfirmOpen(true);
  }

  function cancelConfirm(): void {
    setConfirmOpen(false);
    setPendingFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  async function confirmRestore(): Promise<void> {
    const file = pendingFile;
    if (!file) {
      cancelConfirm();
      return;
    }
    setConfirmOpen(false);
    setRestoring(true);
    try {
      const form = new FormData();
      form.append('file', file);
      await apiPostForm<{ ok: true }>('/api/admin/restore', form);
      toast.push('success', 'Database restored — reloading…');
      window.setTimeout(() => {
        window.location.reload();
      }, 800);
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : 'Restore failed';
      toast.push('error', message);
      setRestoring(false);
      setPendingFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }

  const buttonBaseStyle = {
    background: 'var(--accent)',
    color: 'var(--accent-contrast, white)',
    borderColor: 'var(--accent)',
  } as const;

  return (
    <section
      aria-label="Database Backup and Restore"
      data-testid="database-section"
      className="rounded-2xl border p-5 shadow-sm"
      style={{
        background: 'var(--bg-surface)',
        borderColor: 'var(--border)',
        color: 'var(--text-main)',
      }}
    >
      <div className="mb-4 flex items-center gap-2">
        <Database size={20} aria-hidden="true" style={{ color: 'var(--accent)' }} />
        <h2 className="text-lg font-semibold">Database Backup &amp; Restore</h2>
      </div>
      <p className="mb-3 text-sm" style={{ color: 'var(--text-muted)' }}>
        Path:
      </p>
      <code
        className="mb-4 block break-all rounded-md border px-3 py-2 text-xs"
        style={{
          background: 'var(--bg-primary)',
          borderColor: 'var(--border)',
          color: 'var(--text-main)',
        }}
        data-testid="db-path"
      >
        {DB_PATH}
      </code>
      <div className="flex flex-col gap-2">
        <a
          ref={anchorRef}
          href={BACKUP_URL}
          download="chefcook-backup.sqlite"
          aria-hidden="true"
          tabIndex={-1}
          style={{ display: 'none' }}
          data-testid="backup-download-anchor"
        >
          download
        </a>
        <input
          ref={fileInputRef}
          type="file"
          accept=".sqlite,.db"
          onChange={onFileChange}
          style={{ display: 'none' }}
          data-testid="restore-file-input"
        />
        <button
          type="button"
          onClick={onDownload}
          disabled={downloading || restoring}
          data-testid="backup-download-button"
          className="flex w-full items-center justify-center rounded-md border px-3 py-2 text-sm font-medium"
          style={{
            ...buttonBaseStyle,
            minHeight: 48,
            opacity: downloading ? 0.7 : 1,
          }}
        >
          {downloading ? (
            <Loader2 size={16} className="mr-2 animate-spin" aria-hidden="true" />
          ) : null}
          {downloading ? 'Preparing…' : 'Download SQLite Backup'}
        </button>
        <button
          type="button"
          onClick={openRestorePicker}
          disabled={downloading || restoring}
          data-testid="backup-restore-button"
          className="flex w-full items-center justify-center rounded-md border px-3 py-2 text-sm font-medium"
          style={{
            ...buttonBaseStyle,
            minHeight: 48,
            opacity: restoring ? 0.7 : 1,
          }}
        >
          {restoring ? (
            <Loader2 size={16} className="mr-2 animate-spin" aria-hidden="true" />
          ) : null}
          {restoring ? 'Restoring…' : 'Restore Backup File'}
        </button>
      </div>

      <Dialog
        open={confirmOpen}
        onClose={cancelConfirm}
        title="Replace current database?"
      >
        <p className="mb-4 text-sm" style={{ color: 'var(--text-muted)' }}>
          The current database will be permanently replaced by{' '}
          <strong style={{ color: 'var(--text-main)' }}>
            {pendingFile?.name ?? 'selected file'}
          </strong>
          . This action cannot be undone.
        </p>
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={cancelConfirm}
            data-testid="restore-cancel-button"
            className="rounded-md border px-3 py-2 text-sm font-medium"
            style={{
              background: 'var(--bg-primary)',
              borderColor: 'var(--border)',
              color: 'var(--text-main)',
              minHeight: 44,
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={confirmRestore}
            data-testid="restore-confirm-button"
            className="rounded-md border px-3 py-2 text-sm font-medium"
            style={{
              background: 'var(--danger, #c0392b)',
              borderColor: 'var(--danger, #c0392b)',
              color: 'white',
              minHeight: 44,
            }}
          >
            Replace database
          </button>
        </div>
      </Dialog>
    </section>
  );
}